import { waitForCanPlay } from '@/lib/audioUtils';

interface MediaSessionConfig {
  title: string;
  artist: string;
  artwork: string | null;
}

interface MediaSessionCallbacks {
  onPlay: () => void;
  onPause: () => void;
  onSeek: (time: number) => void;
}

/**
 * Sets up the Media Session API for lock-screen / notification-area controls.
 * Handles play, pause, seek ±15s, and seek-to actions.
 */
export function setupMediaSession(
  audio: HTMLAudioElement,
  config: MediaSessionConfig,
  callbacks: MediaSessionCallbacks,
  speed: number,
) {
  if (!('mediaSession' in navigator)) return;

  navigator.mediaSession.metadata = new MediaMetadata({
    title: config.title || 'Brief Summary',
    artist: config.artist || 'Pod Brief',
    album: 'Pod Brief',
    artwork: config.artwork
      ? [{ src: config.artwork, sizes: '512x512', type: 'image/jpeg' }]
      : [
          { src: '/icon-192.png', sizes: '192x192', type: 'image/png' },
          { src: '/icon-512.png', sizes: '512x512', type: 'image/png' },
        ],
  });

  navigator.mediaSession.setActionHandler('play', async () => {
    try {
      // iOS may suspend audio when backgrounded — reload if needed
      if (audio.readyState < 2 && audio.src) {
        const savedPosition = audio.currentTime;
        audio.load();
        await waitForCanPlay(audio, 5000);
        audio.currentTime = savedPosition;
        audio.playbackRate = speed;
      }
      await audio.play();
      callbacks.onPlay();
      navigator.mediaSession.playbackState = 'playing';
    } catch (e) {
      console.error('[MediaSession] Play failed:', e);
    }
  });

  navigator.mediaSession.setActionHandler('pause', () => {
    audio.pause();
    callbacks.onPause();
    navigator.mediaSession.playbackState = 'paused';
  });

  navigator.mediaSession.setActionHandler('seekbackward', () => {
    const newTime = Math.max(0, audio.currentTime - 15);
    audio.currentTime = newTime;
    callbacks.onSeek(newTime);
  });

  navigator.mediaSession.setActionHandler('seekforward', () => {
    const newTime = Math.min(audio.duration || 0, audio.currentTime + 15);
    audio.currentTime = newTime;
    callbacks.onSeek(newTime);
  });

  navigator.mediaSession.setActionHandler('seekto', (details) => {
    if (details.seekTime === undefined) return;
    audio.currentTime = details.seekTime;
    callbacks.onSeek(details.seekTime);
  });

  updatePositionState(audio);
}

/** Sync the position state shown on lock-screen / notification controls */
export function updatePositionState(audio: HTMLAudioElement) {
  if (!('mediaSession' in navigator)) return;
  if (!audio.duration || audio.duration <= 0) return;
  if (!('setPositionState' in navigator.mediaSession)) return;

  try {
    navigator.mediaSession.setPositionState({
      duration: audio.duration,
      playbackRate: audio.playbackRate,
      position: Math.min(audio.currentTime, audio.duration),
    });
  } catch {
    // Ignore — some browsers reject invalid states
  }
}
